/**
 * Deterministic branded placeholder for public lesson cards without a cover image.
 */

function hashString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

/** First letters of the first 1–2 words (e.g. "Kids Summer Camp" → "KS"). */
export function getLessonMonogram(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) {
    return words[0]!.slice(0, 2).toUpperCase();
  }
  return `${words[0]![0] ?? ''}${words[1]![0] ?? ''}`.toUpperCase();
}

function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.replace('#', '').trim();
  if (normalized.length !== 3 && normalized.length !== 6) return null;
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b };
}

function hslToHex(h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function blendHex(baseHex: string, accentHex: string, weight: number): string {
  const base = parseHexColor(baseHex);
  const accent = parseHexColor(accentHex);
  if (!base || !accent) return baseHex;
  const w = Math.min(1, Math.max(0, weight));
  const r = Math.round(base.r * (1 - w) + accent.r * w);
  const g = Math.round(base.g * (1 - w) + accent.g * w);
  const b = Math.round(base.b * (1 - w) + accent.b * w);
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

export type LessonCoverPlaceholderStyle = {
  monogram: string;
  gradientFrom: string;
  gradientTo: string;
};

/**
 * Stable gradient + monogram from lesson name; blends toward academy primary when set.
 */
export function getLessonCoverPlaceholderStyle(
  name: string,
  accentColor?: string | null
): LessonCoverPlaceholderStyle {
  const hash = hashString(name.trim().toLowerCase() || 'lesson');
  const hue = hash % 360;
  const from = hslToHex(hue, 0.55, 0.42);
  const to = hslToHex((hue + 40) % 360, 0.6, 0.28);

  if (accentColor && parseHexColor(accentColor)) {
    return {
      monogram: getLessonMonogram(name),
      gradientFrom: blendHex(from, accentColor, 0.35),
      gradientTo: blendHex(to, accentColor, 0.5),
    };
  }

  return {
    monogram: getLessonMonogram(name),
    gradientFrom: from,
    gradientTo: to,
  };
}
